import { Calculator } from 'lucide-react';
import ActionButton from '../../../components/Buttons/ActionButton';
import SearchInputField from '../../../components/Forms/Input/SearchInputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import type { RestaurantPurchaseOrderDTO } from '../../../types/restaurant/RestaurantPurchaseOrderTypes';
import type { ProductionPlanningSource } from '../../../types/restaurant/RestaurantProductionPlannerTypes';
import { formatMoney } from '../../../utils/decimalValue';

interface Props {
  source: ProductionPlanningSource;
  purchaseId: number | '';
  purchases: RestaurantPurchaseOrderDTO[];
  loading: boolean;
  loadingOptions: boolean;
  search: string;
  categoryId: number;
  categories: Array<{ id: number; name: string }>;
  onSourceChange: (source: ProductionPlanningSource) => void;
  onPurchaseChange: (purchaseId: number | '') => void;
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: number) => void;
  onCalculate: () => void;
}

const sourceOptions = [
  { value: 'INVENTORY', label: 'Inventario actual' },
  { value: 'PURCHASE', label: 'Compra específica' },
  { value: 'INVENTORY_AND_PURCHASE', label: 'Inventario + compra' },
];

const ProductionPlannerFilters = ({
  source,
  purchaseId,
  purchases,
  loading,
  loadingOptions,
  search,
  categoryId,
  categories,
  onSourceChange,
  onPurchaseChange,
  onSearchChange,
  onCategoryChange,
  onCalculate,
}: Props) => {
  const usesPurchase = source !== 'INVENTORY';

  return (
    <div className="rounded-xl border border-stroke bg-gray-2/40 p-5 dark:border-strokedark dark:bg-meta-4/20">
      <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 xl:grid-cols-5 xl:items-end">
        <SelectField
          label="Fuente de disponibilidad"
          name="plannerSource"
          value={source}
          iconKey="inventory"
          options={sourceOptions}
          onChange={(event) =>
            onSourceChange(event.target.value as ProductionPlanningSource)
          }
        />
        {usesPurchase ? (
          <SelectField
            label="Compra para planificar"
            name="plannerPurchase"
            value={purchaseId}
            iconKey="purchase"
            disabled={loadingOptions}
            options={purchases.map((purchase) => ({
              value: purchase.id,
              label: `${purchase.purchaseNumber} · ${purchase.supplierName} · ${
                purchase.status === 'CONFIRMED' ? 'Confirmada' : 'Borrador'
              } · ${formatMoney(purchase.total)}`,
            }))}
            onChange={(event) => onPurchaseChange(Number(event.target.value))}
          />
        ) : (
          <div />
        )}
        <div className="xl:pb-5">
          <SearchInputField
            label="Buscar"
            name="plannerSearch"
            value={search}
            iconKey="search"
            placeholder="Receta, plato o producto..."
            withMargin={false}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
        <SelectField
          label="Categoría"
          name="plannerCategory"
          value={categoryId}
          options={[
            { value: 0, label: 'Todas las categorías' },
            ...categories.map((category) => ({
              value: category.id,
              label: category.name,
            })),
          ]}
          onChange={(event) => onCategoryChange(Number(event.target.value))}
        />
        <ActionButton
          label="Calcular producción posible"
          loading={loading}
          loadingLabel="Calculando..."
          icon={<Calculator className="h-5 w-5" />}
          variant="primary"
          size="lg"
          fullWidth={false}
          disabled={usesPurchase && !purchaseId}
          onClick={onCalculate}
          className="mb-5 w-full whitespace-nowrap xl:w-auto"
        />
      </div>
      <p className="text-xs text-bodydark2">
        {source === 'INVENTORY'
          ? 'Calcula únicamente con las existencias actuales.'
          : source === 'PURCHASE'
          ? 'Aísla las materias primas contenidas en la compra seleccionada.'
          : 'Combina inventario y compra sin duplicar existencias confirmadas.'}
      </p>
    </div>
  );
};

export default ProductionPlannerFilters;
